import cloudinaryConfig from '../cloudinary.config';

/**
 * Uploads an image to Cloudinary
 * @param {File} file - The image file to upload
 * @param {Function} progressCallback - Callback function to track upload progress
 * @returns {Promise<string>} - The URL of the uploaded image
 */
export const uploadImageToCloudinary = (file, progressCallback = () => {}) => {
  return new Promise((resolve, reject) => {
    // Create a FormData instance
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', cloudinaryConfig.uploadPreset);
    formData.append('folder', cloudinaryConfig.folder);
    
    // Create an XMLHttpRequest
    const xhr = new XMLHttpRequest();
    
    // Configure the request
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/image/upload`, true);
    
    // Track upload progress
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        progressCallback(progress);
      }
    };
    
    // Handle response
    xhr.onload = () => {
      if (xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);
        resolve(response.secure_url);
      } else {
        reject(new Error('Upload failed'));
      }
    };
    
    // Handle errors
    xhr.onerror = () => {
      reject(new Error('Network error'));
    };
    
    // Send the request
    xhr.send(formData);
  });
};

/**
 * Uploads an audio file to Cloudinary
 * @param {File} file - The audio file to upload
 * @param {Function} progressCallback - Callback function to track upload progress
 * @returns {Promise<string>} - The URL of the uploaded audio file
 */
export const uploadAudioToCloudinary = (file, progressCallback = () => {}) => {
  return new Promise((resolve, reject) => {
    // Create a FormData instance
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', cloudinaryConfig.uploadPreset);
    formData.append('folder', 'audio');
    formData.append('resource_type', 'auto'); // Important for non-image files
    
    // Create an XMLHttpRequest
    const xhr = new XMLHttpRequest();
    
    // Configure the request
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/upload`, true);
    
    // Track upload progress
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        progressCallback(progress);
      }
    };
    
    // Handle response
    xhr.onload = () => {
      if (xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);
        resolve(response.secure_url);
      } else {
        reject(new Error('Audio upload failed'));
      }
    };
    
    // Handle errors
    xhr.onerror = () => {
      reject(new Error('Network error during audio upload'));
    };
    
    // Send the request
    xhr.send(formData);
  });
};

/**
 * Generates a Cloudinary URL with transformations
 * @param {string} url - The original Cloudinary URL
 * @param {Object} options - Transformation options
 * @returns {string} - The transformed URL
 */
export const getOptimizedImageUrl = (url, options = {}) => {
  if (!url || !url.includes('cloudinary.com')) {
    return url;
  }
  
  // Default transformations
  const defaultOptions = {
    width: options.width || 800,
    height: options.height || 600,
    crop: options.crop || 'fill',
    quality: options.quality || 'auto',
    fetchFormat: options.fetchFormat || 'auto'
  };
  
  // Extract the base URL and the path
  const [baseUrl, path] = url.split('/image/upload/');
  
  // Build the transformation string
  const transformations = [
    `w_${defaultOptions.width}`,
    `h_${defaultOptions.height}`,
    `c_${defaultOptions.crop}`,
    `q_${defaultOptions.quality}`,
    `f_${defaultOptions.fetchFormat}`
  ].join(',');
  
  // Return the transformed URL
  return `${baseUrl}/image/upload/${transformations}/${path}`;
};
